import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, ArrowLeft } from "lucide-react";

export default function Login() {
  const { clientLogin, forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(
    new URLSearchParams(window.location.search).get("expired") ? "Session expired — Please sign in again" : ""
  );
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot
  const [notice, setNotice] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setBusy(true);
    try {
      if (mode === "login") {
        await clientLogin(email, password);
        navigate("/portal");
      } else {
        await forgotPassword(email);
        setNotice("If an approved ECOREXS account matches this email, a secure link has been sent.");
        setMode("login");
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Access is available to approved ECOREXS customers only.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col text-[#F4FFF9]"
      style={{ background: "radial-gradient(130% 90% at 50% 0%, #0D3B2D 0%, #071A13 45%, #030B08 100%)" }}>
      <div className="p-6">
        <button onClick={() => navigate("/")} data-testid="login-home-link" className="flex items-center">
          <img src={LOGO.wordmark} alt="ECOREXS" className="w-[160px] h-auto object-contain" />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <form onSubmit={submit} className="w-full max-w-md bg-white/[0.04] border border-[#67F2B3]/20 backdrop-blur-xl rounded-2xl p-8 animate-fade-up" data-testid="client-login-form">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-[#67F2B3]/12 border border-[#67F2B3]/25 mb-5">
            <ShieldCheck className="h-6 w-6 text-[#67F2B3]" />
          </div>
          <h1 className="font-heading font-bold text-2xl">{mode === "login" ? "Client Login" : "Reset Password"}</h1>
          <p className="text-sm text-[#F4FFF9]/60 mt-2 mb-6">Access is available to approved ECOREXS customers only.</p>

          {error && <div className="mb-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
          {notice && <div className="mb-4 text-sm text-[#67F2B3] bg-[#67F2B3]/10 border border-[#67F2B3]/30 rounded-lg px-3 py-2" data-testid="login-notice">{notice}</div>}

          <div className="space-y-4">
            <div>
              <Label className="text-[#F4FFF9]/80">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="client-email-input"
                className="bg-white/5 border-[#67F2B3]/20 text-white placeholder:text-white/30 focus:border-[#67F2B3]" placeholder="you@company.com" />
            </div>
            {mode === "login" && (
              <div>
                <Label className="text-[#F4FFF9]/80">Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="client-password-input"
                  className="bg-white/5 border-[#67F2B3]/20 text-white placeholder:text-white/30 focus:border-[#67F2B3]" placeholder="••••••••" />
              </div>
            )}
          </div>
          <Button type="submit" disabled={busy} data-testid="client-login-submit"
            className="w-full mt-6 bg-[#67F2B3] hover:bg-[#8affca] text-[#071A13] font-semibold py-6 rounded-xl">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "login" ? "Sign In" : "Send Reset Link")}
          </Button>
          <div className="mt-5 text-center text-xs text-[#F4FFF9]/50">
            {mode === "login" ? (
              <button type="button" onClick={() => { setMode("forgot"); setError(""); }} data-testid="forgot-password-link" className="hover:text-[#67F2B3]">Forgot password?</button>
            ) : (
              <button type="button" onClick={() => { setMode("login"); setError(""); }} className="hover:text-[#67F2B3]">Back to sign in</button>
            )}
          </div>
          <button type="button" onClick={() => navigate("/")} data-testid="login-back-home"
            className="w-full mt-3 flex items-center justify-center gap-1.5 text-xs text-[#F4FFF9]/50 hover:text-[#67F2B3] transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </button>
        </form>
      </div>
    </div>
  );
}
